import { jsxs, jsx } from "react/jsx-runtime";
import { forwardRef } from "react";
import PropTypes from "prop-types";
import { includes } from "lodash/fp";
import { SelectButtonOuter, SelectButtonButton, SelectButtonInner, SelectButtonText, SelectButtonIcon, SelectButtonPillInner, SelectButtonPillOuter } from "./select-button.styles.es.js";
import { Icon } from "../icon/icon.es.js";
import { Pill } from "../pill/pill.es.js";
import "@reach/auto-id";
import { useHandler } from "../../hooks/use-handler.es.js";
import "styled-components";
import "../../contexts/display-mode-context/display-mode-context.es.js";
import "../../contexts/global-edit-context/global-edit-context.es.js";
import "../../contexts/toast-context/toast-context.es.js";
import "../../utils/is-valid-ref.es.js";
import "../../hooks/use-horizontal-scroll.es.js";
import "react-use-measure";
import "@juggle/resize-observer";
import "@reach/utils";
import "@reach/rect";
import "compute-scroll-into-view";
const propTypes = {
  appearance: PropTypes.oneOf(["bare", "cell", "default", "grid", "inline"]),
  children: PropTypes.node,
  count: PropTypes.number,
  disabled: PropTypes.bool,
  expanded: PropTypes.bool,
  magnitude: PropTypes.oneOf(["small", "medium", "large"]),
  onExpand: PropTypes.func.isRequired,
  placeholder: PropTypes.string,
  tone: PropTypes.oneOf(["critical", "neutral"])
};
const defaultProps = {
  appearance: void 0,
  children: void 0,
  count: void 0,
  disabled: false,
  expanded: false,
  magnitude: "large",
  placeholder: void 0,
  tone: "neutral"
};
const SelectButton = forwardRef(
  ({
    appearance,
    children,
    count,
    disabled,
    expanded,
    magnitude,
    onExpand,
    placeholder,
    tone,
    ...restProps
  }, ref) => {
    const handleClick = useHandler(() => {
      onExpand(!expanded);
    });
    const handleFocus = useHandler((event) => {
      if (event.target === event.currentTarget) {
        onExpand(false);
      }
    });
    const handleKeyDown = useHandler((event) => {
      if (includes(event.key, ["ArrowDown", "ArrowUp", "Enter", " "])) {
        onExpand(true);
      }
    });
    const isInline = appearance === "inline";
    return /* @__PURE__ */ jsxs(SelectButtonOuter, { children: [
      /* @__PURE__ */ jsx(
        SelectButtonButton,
        {
          ref,
          ...restProps,
          "aria-disabled": disabled || restProps["aria-disabled"] || void 0,
          "aria-expanded": expanded || restProps["aria-expanded"] || void 0,
          onFocus: disabled ? void 0 : handleFocus,
          onKeyDown: disabled ? void 0 : handleKeyDown,
          $appearance: appearance,
          $tone: tone,
          children: /* @__PURE__ */ jsxs(
            SelectButtonInner,
            {
              onClick: disabled ? void 0 : handleClick,
              tabIndex: -1,
              $compact: isInline,
              $magnitude: magnitude,
              children: [
                /* @__PURE__ */ jsx(
                  SelectButtonText,
                  {
                    "data-testid": "select-button-text",
                    disabled,
                    $compact: isInline,
                    $isPlaceholder: !children,
                    children: children || placeholder
                  }
                ),
                count === void 0 ? /* @__PURE__ */ jsx(SelectButtonIcon, { $compact: isInline, children: /* @__PURE__ */ jsx(
                  Icon,
                  {
                    magnitude: "xsmall",
                    transform: expanded ? "rotate(180deg)" : void 0,
                    type: "down"
                  }
                ) }) : !isInline && /* @__PURE__ */ jsx(SelectButtonPillInner, { children: /* @__PURE__ */ jsx(Pill, { children: count }) })
              ]
            }
          )
        }
      ),
      isInline && count !== void 0 && /* @__PURE__ */ jsx(SelectButtonPillOuter, { children: /* @__PURE__ */ jsx(Pill, { children: count }) })
    ] });
  }
);
SelectButton.propTypes = propTypes;
SelectButton.defaultProps = defaultProps;
export {
  SelectButton
};
